// SubPanel-Elect Security Service
// Comprehensive security and compliance management functionality

import { apiClient } from '@/services/shared/apiClient';
import type {
  SecurityOverview,
  SecurityStatus,
  ComplianceStatus,
  ThreatAnalysis,
  SecurityAudit,
  SecurityIncident,
  ComplianceFramework,
  ComplianceAssessment,
  SecurityPolicy,
  VulnerabilityAssessment,
  SecurityRecommendation,
  ThreatIntelligence,
  ThreatMonitoring,
  SubpanelElectApiResponse,
} from '@/types/entities/community_subpanel-elect';

export class SecurityService {
  /**
   * Get comprehensive security overview
   */
  async getSecurityOverview(): Promise<SecurityOverview> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<SecurityOverview>>(
        '/subpanel-elect/security/overview',
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch security overview:', error);
      return this.getMockSecurityOverview();
    }
  }

  /**
   * Get current security status
   */
  async getSecurityStatus(): Promise<SecurityStatus> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<SecurityStatus>>(
        '/subpanel-elect/security/status',
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch security status:', error);
      return this.getMockSecurityStatus();
    }
  }

  /**
   * Get compliance status
   */
  async getComplianceStatus(framework?: string): Promise<ComplianceStatus> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<ComplianceStatus>>(
        '/subpanel-elect/security/compliance',
        { framework },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch compliance status:', error);
      return this.getMockComplianceStatus();
    }
  }

  /**
   * Get threat analysis
   */
  async getThreatAnalysis(timeframe: '24h' | '7d' | '30d' = '24h'): Promise<ThreatAnalysis> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<ThreatAnalysis>>(
        '/subpanel-elect/security/threats',
        { timeframe },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch threat analysis:', error);
      return this.getMockThreatAnalysis();
    }
  }

  /**
   * Get security audits
   */
  async getSecurityAudits(filters?: {
    status?: string[];
    type?: string[];
    dateRange?: { start: string; end: string };
    limit?: number;
  }): Promise<SecurityAudit[]> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<SecurityAudit[]>>(
        '/subpanel-elect/security/audits',
        filters,
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch security audits:', error);
      return this.getMockSecurityAudits();
    }
  }

  /**
   * Get security incidents
   */
  async getSecurityIncidents(
    status?: 'open' | 'investigating' | 'resolved' | 'closed',
    timeframe: '24h' | '7d' | '30d' = '30d',
  ): Promise<SecurityIncident[]> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<SecurityIncident[]>>(
        '/subpanel-elect/security/incidents',
        { status, timeframe },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch security incidents:', error);
      return this.getMockSecurityIncidents();
    }
  }

  /**
   * Get vulnerability assessments
   */
  async getVulnerabilityAssessments(
    severity?: 'critical' | 'high' | 'medium' | 'low',
  ): Promise<VulnerabilityAssessment[]> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<VulnerabilityAssessment[]>>(
        '/subpanel-elect/security/vulnerabilities',
        { severity },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch vulnerability assessments:', error);
      return this.getMockVulnerabilityAssessments();
    }
  }

  /**
   * Get security policies
   */
  async getSecurityPolicies(): Promise<SecurityPolicy[]> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<SecurityPolicy[]>>(
        '/subpanel-elect/security/policies',
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch security policies:', error);
      return this.getMockSecurityPolicies();
    }
  }

  /**
   * Get security recommendations
   */
  async getSecurityRecommendations(category?: string): Promise<SecurityRecommendation[]> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<SecurityRecommendation[]>>(
        '/subpanel-elect/security/recommendations',
        { category },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch security recommendations:', error);
      return this.getMockSecurityRecommendations();
    }
  }

  /**
   * Get threat intelligence
   */
  async getThreatIntelligence(): Promise<ThreatIntelligence> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<ThreatIntelligence>>(
        '/subpanel-elect/security/threat-intelligence',
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch threat intelligence:', error);
      return this.getMockThreatIntelligence();
    }
  }

  /**
   * Get threat monitoring
   */
  async getThreatMonitoring(): Promise<ThreatMonitoring> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<ThreatMonitoring>>(
        '/subpanel-elect/security/threat-monitoring',
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch threat monitoring:', error);
      return this.getMockThreatMonitoring();
    }
  }

  /**
   * Get compliance frameworks
   */
  async getComplianceFrameworks(): Promise<ComplianceFramework[]> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<ComplianceFramework[]>>(
        '/subpanel-elect/security/compliance/frameworks',
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch compliance frameworks:', error);
      return this.getMockComplianceFrameworks();
    }
  }

  /**
   * Get compliance assessments
   */
  async getComplianceAssessments(framework?: string): Promise<ComplianceAssessment[]> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<ComplianceAssessment[]>>(
        '/subpanel-elect/security/compliance/assessments',
        { framework },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch compliance assessments:', error);
      return this.getMockComplianceAssessments();
    }
  }

  /**
   * Create security incident
   */
  async createSecurityIncident(incidentData: Partial<SecurityIncident>): Promise<SecurityIncident> {
    try {
      const response = await apiClient.post<SubpanelElectApiResponse<SecurityIncident>>(
        '/subpanel-elect/security/incidents',
        incidentData,
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to create security incident:', error);
      throw error;
    }
  }

  /**
   * Update security incident
   */
  async updateSecurityIncident(
    incidentId: string,
    updates: Partial<SecurityIncident>,
  ): Promise<SecurityIncident> {
    try {
      const response = await apiClient.put<SubpanelElectApiResponse<SecurityIncident>>(
        `/subpanel-elect/security/incidents/${incidentId}`,
        updates,
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to update security incident:', error);
      throw error;
    }
  }

  /**
   * Resolve security incident
   */
  async resolveSecurityIncident(incidentId: string, resolution: string): Promise<SecurityIncident> {
    try {
      const response = await apiClient.put<SubpanelElectApiResponse<SecurityIncident>>(
        `/subpanel-elect/security/incidents/${incidentId}/resolve`,
        { resolution },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to resolve security incident:', error);
      throw error;
    }
  }

  /**
   * Update security policy
   */
  async updateSecurityPolicy(
    policyId: string,
    updates: Partial<SecurityPolicy>,
  ): Promise<SecurityPolicy> {
    try {
      const response = await apiClient.put<SubpanelElectApiResponse<SecurityPolicy>>(
        `/subpanel-elect/security/policies/${policyId}`,
        updates,
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to update security policy:', error);
      throw error;
    }
  }

  /**
   * Acknowledge security recommendation
   */
  async acknowledgeSecurityRecommendation(recommendationId: string): Promise<void> {
    try {
      await apiClient.put(
        `/subpanel-elect/security/recommendations/${recommendationId}/acknowledge`,
      );
    } catch (error) {
      console.error('Failed to acknowledge security recommendation:', error);
      throw error;
    }
  }

  /**
   * Implement security recommendation
   */
  async implementSecurityRecommendation(
    recommendationId: string,
    implementationNotes: string,
  ): Promise<void> {
    try {
      await apiClient.put(
        `/subpanel-elect/security/recommendations/${recommendationId}/implement`,
        { implementationNotes },
      );
    } catch (error) {
      console.error('Failed to implement security recommendation:', error);
      throw error;
    }
  }

  /**
   * Run security scan
   */
  async runSecurityScan(
    scanType: 'vulnerability' | 'compliance' | 'full',
  ): Promise<{ scanId: string }> {
    try {
      const response = await apiClient.post<SubpanelElectApiResponse<{ scanId: string }>>(
        '/subpanel-elect/security/scan',
        { scanType },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to run security scan:', error);
      throw error;
    }
  }

  // Mock data methods for development
  private getMockSecurityOverview(): SecurityOverview {
    return {
      status: this.getMockSecurityStatus(),
      threats: this.getMockThreatAnalysis(),
      compliance: this.getMockComplianceStatus(),
      audits: this.getMockSecurityAudits(),
      policies: this.getMockSecurityPolicies(),
      incidents: this.getMockSecurityIncidents(),
    };
  }

  private getMockSecurityStatus(): SecurityStatus {
    return {
      overall: 'secure',
      score: 87,
      lastAssessment: new Date().toISOString(),
      components: [
        {
          name: 'Authentication',
          type: 'authentication',
          status: 'secure',
          score: 89,
          lastChecked: new Date().toISOString(),
          issues: [],
        },
        {
          name: 'Network Security',
          type: 'network',
          status: 'warning',
          score: 85,
          lastChecked: new Date().toISOString(),
          issues: [],
        },
      ],
      vulnerabilities: {
        total: 12,
        critical: 2,
        high: 4,
        medium: 5,
        low: 1,
        categories: [
          { category: 'Infrastructure', count: 6, severity: 'medium' },
          { category: 'Application', count: 4, severity: 'high' },
          { category: 'Network', count: 2, severity: 'critical' },
        ],
        trends: [
          { period: '2024-12', discovered: 5, resolved: 8, outstanding: 12 },
          { period: '2024-11', discovered: 8, resolved: 6, outstanding: 15 },
        ],
      },
      recommendations: [
        {
          id: 'rec_1',
          title: 'Enable Multi-Factor Authentication',
          description: 'Implement MFA for all admin accounts',
          priority: 'high',
          category: 'authentication',
          impact: 'Reduces risk of unauthorized access',
          effort: 'low',
          cost: 500,
          timeline: '2 weeks',
          implemented: false,
        },
      ],
    };
  }

  private getMockComplianceStatus(): ComplianceStatus {
    return {
      overall: 'partial',
      score: 0,
      frameworks: [
        {
          id: 'soc2',
          name: 'SOC 2 Type II (not certified)',
          type: 'certification',
          description: 'Target framework. No SOC 2 audit has been performed.',
          version: '2017',
          applicability: ['security', 'availability'],
          status: 'non_compliant',
          score: 0,
          requirements: ['CC6.1', 'CC6.2', 'CC6.3'],
          lastAssessment: '',
          nextAssessment: '',
        },
        {
          id: 'gdpr',
          name: 'GDPR',
          type: 'regulatory',
          description: 'General Data Protection Regulation',
          version: '2018',
          applicability: ['data_protection', 'privacy'],
          status: 'partial',
          score: 89,
          requirements: ['art_25', 'art_32', 'art_33'],
          lastAssessment: '2024-11-01T00:00:00Z',
          nextAssessment: '2025-05-01T00:00:00Z',
        },
      ],
      requirements: [
        {
          id: 'req_1',
          frameworkId: 'soc2',
          code: 'CC6.1',
          title: 'Logical Access Controls',
          description: 'Controls over logical access to systems and data',
          category: 'access_control',
          priority: 'high',
          status: 'non_compliant',
          evidence: [],
          controls: ['mfa', 'rbac'],
          gaps: [],
          lastReview: '2024-12-01T00:00:00Z',
          reviewer: 'Security Manager',
          dueDate: '2025-06-01T00:00:00Z',
        },
      ],
      assessments: [],
      remediation: [],
    };
  }

  private getMockThreatAnalysis(): ThreatAnalysis {
    return {
      landscape: {
        summary: {
          total: 45,
          active: 12,
          mitigated: 28,
          accepted: 5,
        },
        categories: [
          { category: 'malware', count: 12, riskLevel: 'medium' },
          { category: 'phishing', count: 8, riskLevel: 'high' },
          { category: 'ddos', count: 3, riskLevel: 'low' },
        ],
        trends: [
          { period: '2024-12', emerging: 5, resolved: 8, escalated: 2 },
          { period: '2024-11', emerging: 8, resolved: 6, escalated: 1 },
        ],
      },
      intelligence: [
        {
          id: 'threat_1',
          type: 'phishing',
          title: 'Holiday Phishing Campaign',
          description: 'Coordinated phishing targeting financial services',
          severity: 'high',
          likelihood: 'medium',
          impact: 'high',
          indicators: [
            {
              type: 'domain',
              value: 'malicious-site.com',
              confidence: 0.95,
              context: 'Known phishing domain',
            },
          ],
          mitigations: ['Email filtering', 'User training'],
          source: 'Threat Intelligence Feed',
          confidence: 0.9,
          publishedAt: '2024-12-15T00:00:00Z',
        },
      ],
      modeling: [],
      monitoring: {
        realTime: {
          status: 'active',
          coverage: 95.2,
          sources: [
            {
              name: 'SIEM',
              type: 'siem',
              status: 'active',
              lastUpdate: '2024-12-15T16:00:00Z',
              eventsPerHour: 1250,
              accuracy: 0.92,
            },
          ],
          metrics: {
            eventsProcessed: 125000,
            alertsGenerated: 45,
            falsePositives: 8,
            responseTime: 2.5,
            detectionRate: 0.95,
          },
        },
        alerts: [],
        investigations: [],
        response: {
          playbooks: [],
          procedures: [],
          team: {
            members: [
              {
                id: 'analyst_1',
                name: 'Security Analyst 1',
                role: 'analyst',
                skills: ['incident_response', 'malware_analysis'],
                certifications: [],
                availability: 'available',
                contactMethods: [],
              },
            ],
            roles: [],
            contactInfo: [],
            availability: [],
          },
          training: [],
        },
      },
    };
  }

  private getMockSecurityAudits(): SecurityAudit[] {
    return [
      {
        id: 'audit_1',
        type: 'internal',
        name: 'Q4 2024 Security Assessment',
        description: 'Comprehensive security audit covering access controls and infrastructure',
        scope: ['infrastructure', 'applications', 'policies'],
        auditor: 'Internal Security Team',
        organization: 'ToolboxLogic',
        methodology: ['interviews', 'documentation_review', 'technical_testing'],
        standards: ['Internal security checklist'],
        startDate: '2024-12-01T00:00:00Z',
        endDate: '2024-12-10T00:00:00Z',
        status: 'completed',
        findings: [
          {
            id: 'finding_1',
            category: 'access_control',
            title: 'Excessive Privileged Accounts',
            description: 'Several service accounts have elevated privileges beyond requirements',
            severity: 'medium',
            riskRating: 'medium',
            affected: ['service_account_1', 'service_account_2'],
            evidence: ['privilege_audit_report.pdf', 'account_review_logs.txt'],
            impact: 'Potential for privilege escalation',
            likelihood: 'medium',
            recommendation: 'Review and reduce privileges to minimum required',
            status: 'open',
            owner: 'IT Security Manager',
            dueDate: '2025-01-31T00:00:00Z',
          },
        ],
        recommendations: [
          {
            id: 'rec_1',
            findingId: 'finding_1',
            title: 'Implement Principle of Least Privilege',
            description: 'Regularly review and audit user permissions',
            priority: 'high',
            category: 'access_control',
            effort: 'medium',
            cost: 15000,
            timeline: '30 days',
            benefits: ['Reduced attack surface', 'Improved compliance'],
            implementation: ['Conduct access review', 'Update IAM policies'],
            status: 'approved',
          },
        ],
        report: {
          id: 'report_1',
          title: 'Q4 2024 Security Assessment Report',
          executive_summary: 'Overall security posture is strong with identified improvements',
          methodology: 'Risk-based assessment using industry standards',
          scope: 'Enterprise infrastructure and applications',
          findings_summary: '5 findings identified with 1 high priority item',
          recommendations_summary: '3 recommendations for security improvements',
          risk_assessment: 'Medium risk with manageable exposure',
          conclusion: 'Security program is effective with recommended enhancements',
          document: '/reports/q4_2024_security_audit.pdf',
          publishedAt: '2024-12-10T00:00:00Z',
          distribution: ['CISO', 'IT Director', 'Audit Committee'],
        },
        followUp: [],
      },
    ];
  }

  private getMockSecurityIncidents(): SecurityIncident[] {
    return [
      {
        id: 'incident_1',
        title: 'Suspicious Network Activity',
        description: 'Unusual outbound connections detected from production server',
        type: 'attack',
        severity: 'high',
        status: 'investigating',
        discoveredAt: '2024-12-15T14:30:00Z',
        discoveredBy: 'Automated Monitoring System',
        reportedAt: '2024-12-15T14:35:00Z',
        reportedBy: 'Security Team',
        source: 'SIEM Alert',
        affected: [
          {
            id: 'asset_1',
            type: 'system',
            name: 'prod-web-01',
            description: 'Production web server',
            criticality: 'high',
            impact: 'moderate',
            compromised: false,
            dataExposed: false,
            downtime: 0,
          },
        ],
        impact: {
          business: {
            revenue: 0,
            reputation: 'minor',
            operations: 'minor',
            compliance: 'none',
          },
          technical: {
            systems: 1,
            users: 0,
            data: 'none',
            downtime: 0,
          },
          legal: {
            regulations: [],
            penalties: 0,
            litigation: false,
          },
        },
        responders: [
          {
            id: 'resp_1',
            name: 'Security Analyst',
            role: 'Lead Investigator',
            team: 'Security Operations',
            assignedAt: '2024-12-15T14:40:00Z',
            status: 'active',
            contributions: ['Initial analysis', 'System isolation'],
          },
        ],
        timeline: [
          {
            timestamp: '2024-12-15T14:30:00Z',
            event: 'Detection',
            description: 'Unusual network activity detected',
            source: 'SIEM',
            verified: true,
            impact: 'Low - monitoring systems detected anomaly',
          },
        ],
        actions: [
          {
            id: 'action_1',
            type: 'containment',
            description: 'Isolated affected system',
            assignedTo: 'Security Team',
            startedAt: '2024-12-15T15:00:00Z',
            completedAt: '2024-12-15T15:30:00Z',
            status: 'completed',
            results: 'System successfully isolated',
            effectiveness: 85,
          },
        ],
        evidence: [
          {
            id: 'evidence_1',
            type: 'log',
            description: 'Network connection logs',
            location: '/logs/network/2024-12-15.log',
            collectedBy: 'Security Analyst',
            collectedAt: '2024-12-15T15:30:00Z',
            hash: 'sha256:abc123...',
            chainOfCustody: [],
            analysis: 'Network logs show suspicious outbound connections',
          },
        ],
        rootCause: 'Under investigation',
        resolution: 'In progress',
        lessonsLearned: [],
        recommendations: [],
        regulations: [],
        notifications: [],
        costs: [],
        createdAt: '2024-12-15T14:35:00Z',
        updatedAt: '2024-12-15T16:00:00Z',
      },
    ];
  }

  private getMockVulnerabilityAssessments(): VulnerabilityAssessment[] {
    return [
      {
        total: 156,
        critical: 3,
        high: 12,
        medium: 48,
        low: 93,
        categories: [
          { category: 'web_application', count: 45, severity: 'medium' },
          { category: 'network', count: 28, severity: 'high' },
          { category: 'system', count: 31, severity: 'medium' },
          { category: 'database', count: 15, severity: 'low' },
          { category: 'authentication', count: 37, severity: 'high' },
        ],
        trends: [
          { period: '2024-12', discovered: 23, resolved: 18, outstanding: 5 },
          { period: '2024-11', discovered: 31, resolved: 28, outstanding: 3 },
          { period: '2024-10', discovered: 19, resolved: 22, outstanding: -3 },
        ],
      },
    ];
  }

  private getMockSecurityPolicies(): SecurityPolicy[] {
    return [
      {
        id: 'policy_1',
        name: 'Password Policy',
        description: 'Corporate policy defining password requirements and management procedures',
        type: 'administrative',
        scope: ['all_users', 'systems', 'applications'],
        owner: 'Security Team',
        version: '2.1',
        category: 'access_control',
        status: 'active',
        content: {
          purpose: 'Defines requirements for user password creation and management',
          scope: 'All users and systems within the organization',
          definitions: [
            {
              term: 'Strong Password',
              definition:
                'Password with minimum 12 characters including uppercase, lowercase, numbers, and symbols',
            },
          ],
          requirements: [
            'Minimum 12 characters in length',
            'Must include uppercase letters',
            'Must include lowercase letters',
            'Must include numbers',
            'Must include special characters',
          ],
          procedures: [
            'Use organization-approved password manager',
            'Enable multi-factor authentication where available',
            'Report suspected password compromise immediately',
          ],
          roles: [
            {
              role: 'Users',
              responsibilities: ['Create strong passwords', 'Keep passwords confidential'],
            },
          ],
          violations: ['Sharing passwords', 'Using weak passwords', 'Writing passwords down'],
          enforcement: 'Automated password complexity validation and regular audits',
          review: 'Annual review by Security Team',
        },
        enforcement: {
          method: 'automated',
          frequency: 'continuous',
          monitoring: ['password_strength_checks', 'login_failures', 'password_reuse'],
          violations: {
            detection: ['automated_scanning', 'user_reports'],
            notification: ['email_alert', 'dashboard_notification'],
            escalation: ['security_team', 'manager'],
            remediation: ['password_reset', 'training'],
            discipline: ['warning', 'account_suspension'],
          },
          metrics: ['compliance_rate', 'violation_count', 'remediation_time'],
        },
        approver: 'CISO',
        effectiveDate: '2024-11-15T00:00:00Z',
        reviewDate: '2025-11-01T00:00:00Z',
        compliance: [],
        exceptions: [],
        metrics: {
          compliance: 95,
          violations: 3,
          exceptions: 0,
          training: 98,
          lastAssessment: '2024-11-01T00:00:00Z',
          effectiveness: 92,
        },
      },
    ];
  }

  private getMockSecurityRecommendations(): SecurityRecommendation[] {
    return [
      {
        id: 'rec_1',
        title: 'Enable Multi-Factor Authentication for All Admin Accounts',
        description: 'Implement MFA to add an additional layer of security for privileged accounts',
        category: 'access_control',
        priority: 'high',
        impact: 'high',
        effort: 'low',
        cost: 5000,
        timeline: '2 weeks',
        implemented: false,
      },
    ];
  }

  private getMockThreatIntelligence(): ThreatIntelligence {
    return {
      id: 'threat_intel_1',
      type: 'phishing',
      title: 'Coordinated Phishing Campaign Targeting Financial Services',
      description:
        'Large-scale phishing campaign using holiday themes to target financial institutions',
      severity: 'high',
      likelihood: 'medium',
      impact: 'high',
      indicators: [
        {
          type: 'domain',
          value: 'malicious-domain.com',
          confidence: 0.9,
          context: 'Suspicious domain from threat intelligence feed',
        },
      ],
      mitigations: ['Enhanced email filtering', 'User awareness training', 'Domain blocking'],
      source: 'Threat Intelligence Provider',
      confidence: 0.85,
      publishedAt: '2024-12-10T00:00:00Z',
      expiresAt: '2025-01-10T00:00:00Z',
    };
  }

  private getMockThreatMonitoring(): ThreatMonitoring {
    return {
      realTime: {
        status: 'active',
        coverage: 94.5,
        sources: [],
        metrics: {
          eventsProcessed: 125000,
          alertsGenerated: 45,
          falsePositives: 8,
          responseTime: 2.5,
          detectionRate: 0.95,
        },
      },
      alerts: [
        {
          id: 'alert_1',
          type: 'suspicious_activity',
          severity: 'medium',
          title: 'Multiple Failed Login Attempts',
          description: 'Multiple failed login attempts from single IP',
          source: 'Authentication System',
          affected: ['user@company.com'],
          indicators: [],
          timeline: ['2024-12-15T15:30:00Z - First failed attempt detected'],
          status: 'investigating',
          createdAt: '2024-12-15T15:30:00Z',
          updatedAt: '2024-12-15T16:00:00Z',
        },
      ],
      investigations: [],
      response: {
        playbooks: [],
        procedures: [],
        team: {
          members: [],
          roles: [],
          contactInfo: [],
          availability: [],
        },
        training: [],
      },
    };
  }

  private getMockComplianceFrameworks(): ComplianceFramework[] {
    return [
      {
        id: 'soc2',
        name: 'SOC 2 Type II (not certified)',
        type: 'certification',
        description: 'Target framework covering security, availability, and confidentiality',
        version: '2017',
        applicability: ['security', 'availability', 'confidentiality'],
        status: 'non_compliant',
        score: 0,
        requirements: ['CC6.1', 'CC6.2', 'CC6.3'],
        lastAssessment: '',
        nextAssessment: '',
      },
    ];
  }

  private getMockComplianceAssessments(): ComplianceAssessment[] {
    return [
      {
        id: 'assessment_1',
        type: 'internal',
        scope: ['Security', 'Availability'],
        framework: ['Internal review'],
        assessor: 'Internal review',
        organization: 'ToolboxLogic',
        startDate: '',
        endDate: '',
        status: 'planned',
        methodology: 'Internal self-review of current controls',
        findings: [
          {
            id: 'finding_1',
            requirement: 'CC6.1',
            finding: 'Multi-factor authentication is not yet implemented',
            evidence: [],
            rating: 'needs_improvement',
            riskLevel: 'medium',
            impact: 'Accounts rely on password authentication only',
            recommendation: 'Implement multi-factor authentication',
          },
        ],
        recommendations: [
          {
            id: 'rec_1',
            title: 'Enhance Privileged Access Monitoring',
            description: 'Enhance privileged access monitoring',
            priority: 'medium',
            category: 'access_control',
            effort: 'medium',
            cost: 25000,
            timeline: '60 days',
            benefits: ['Improved security posture'],
            risks: ['Implementation complexity'],
          },
        ],
        report: {
          id: 'report_1',
          title: 'Internal Security Review',
          summary: 'Internal review of current controls',
          score: 0,
          status: 'non_compliant',
          strengths: [],
          weaknesses: ['No multi-factor authentication', 'Limited automation'],
          priorities: ['Implement multi-factor authentication'],
          roadmap: ['Implement multi-factor authentication', 'Automate access reviews'],
          document: '',
          publishedAt: '',
        },
      },
    ];
  }
}

export const securityService = new SecurityService();
